import React, { useState, useEffect } from "react";
import getSvgImgSrc from "../../core/utils/getSvgImgSrc";
import ListSvgIcons from "./list-svg-icons";
import UnsavedChanges, { UnsavedChangesManual } from "../../partials/unsaved-changes";

/**
 * Checks a string to see if it contains only alphabetic chars
 * Return true if it contains only letters, numbers, or: " ", -, '
 */
export const checkNoSpecialChars = (str) => {
  let isValid = /^[a-zA-Z0-9 \-']+$/.test(str);
  let value = str;
  let empty = "";
  if (isValid || value === empty) {
    return true;
  }
  return false;
};

/**
 * Checks a string to see if it contains </svg> and viewBox 
 * Returns true if it contains </svg> AND viewBox
 */
export const checkSvg = (svgToCheck) => {
  let hasStartTag = svgToCheck.includes("<svg");
  let hasEndTag = svgToCheck.includes("</svg>");
  let hasViewbox = svgToCheck.includes("viewBox");
  if (hasStartTag && hasEndTag && hasViewbox) {
    return true;
  } else {
    return false;
  }
};

/**
 * Removes <script> tags from a given string
 * Returns the given string without <script> tags
 */
export const removeScriptsFromSvg = (svgCode) => {
  const scriptRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  const svgClean = svgCode.replace(scriptRegex, "");
  return svgClean;
}

export const removeSpecialChars = (str) => {
  let arr = str.split("");
  let filtered = [];
  arr.forEach((c) => {
    if (checkNoSpecialChars(c)) {
      filtered.push(c);
    }
  });
  return filtered.join("");
};

export function SystemIcons(props) {
  const {
    backBtn,
    listOfIcons,
    editingIcon,
    creatingIcon,
    onCreateNew,
    newName,
    newSvg,
    newSave,
    cancel,
    editIconInfo,
    editName,
    editSvg,
    editSave,
    deleteIcon,
    editSvgIcon,
  } = props;

  const [updatedName, setUpdatedName] = useState({
    value: "",
    finalValue: "",
  });
  const [nameError, setNameError] = useState("");
  const [updatedSvg, setUpdatedSvg] = useState("");
  const [editingSvg, setEditingSvg] = useState("");
  const [originalInfo, setOriginalInfo] = useState(editIconInfo);
  const [svgError, setSvgError] = useState("");
  const [createNewDisabled, setCreateNewDisabled] = useState(true);
  const [updateDisabled, setUpdateDisabled] = useState(true);
  const [showManualModel, setShowManualModel] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const onCreateNewName = (v) => {
    if (checkNoSpecialChars(v.target.value)) {
      setNameError("");
      setUpdatedName({ value: v.target.value, finalValue: v });
    } else {
      setNameError("*Special characters are not allowed.");
    }
  };

  const onCreateNewSvg = (s) => {
    if (s.target.value.length > 0) {
      const cleanedSvg = removeScriptsFromSvg(s.target.value);
      const isValidSvg = checkSvg(cleanedSvg);
      if (isValidSvg) {
        setSvgError("");
        setUpdatedSvg(cleanedSvg);
        newSvg({ target: { value: cleanedSvg } });
      } else {
        setSvgError(
          "*Please check the SVG code and make sure it is in a valid format."
        );
      }
    } else {
      setSvgError("");
    }
  };

  const onSaveNewName = () => {
    if (updatedName.finalValue) {
      newName(updatedName.finalValue);
    }
  };

  const onSubmitNew = () => {
    newSave();
    onCancel();
  };

  /**
   * Editing functions
   */

  const onEditName = (v) => {
    if (checkNoSpecialChars(v.target.value)) {
      setNameError("");
      editName(v);
    } else {
      setNameError("*Special characters are not allowed.");
      v.target.value = removeSpecialChars(v.target.value);
      editName(v);
    }
  };

  const onEditSvg = (s) => {
    if (s.target.value.length > 0) {
      const cleanedSvg = removeScriptsFromSvg(s.target.value);
      const isValidSvg = checkSvg(cleanedSvg);
      if (isValidSvg) {
        setSvgError("");
        setEditingSvg(cleanedSvg);
        editSvg({target: {value: cleanedSvg}});
      } else {
        setEditingSvg(s.target.value);
        editSvg(s);
        setSvgError(
          "*Please check the SVG code and make sure it is in a valid format."
        );
      }
    } else {
      setSvgError("");
      setEditingSvg(s.target.value);
      editSvg(s);
    }
  };

  const onSubmitEdit = () => {
    editSave();
    cancel();
  };

  const onCancel = () => {
    setNameError("");
    setSvgError("");
    setCreateNewDisabled(true);
    cancel();
  };

  useEffect(() => {
    const disableCreateNew = () => {
      if (updatedName.value.length < 1 || updatedSvg === "") {
        setCreateNewDisabled(true);
      } else {
        setCreateNewDisabled(false);
      }
    };
    disableCreateNew();
  }, [updatedName, updatedSvg])

  useEffect(() => {
    let didCancel = false;
    const disableUpdate = () => {
      if (!editIconInfo.name.length || !editIconInfo.svg.length) {
        setUpdateDisabled(true); // check fields are not empty
        return;
      } 
      if (originalInfo.name === editIconInfo.name && originalInfo.svg === editIconInfo.svg) {
        setUpdateDisabled(true); // check if values have changed
        return;
      } 
      if (svgError.length) {
        setUpdateDisabled(true); // check it has errors
        return;
      }
      setUpdateDisabled(false);
    };
    if (Object.keys(editIconInfo).length !== 0 && !didCancel) {
      disableUpdate();
    }
    return () => {
      didCancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editIconInfo, originalInfo]);

  useEffect(() => {
    let didCancel = false;
    if(!didCancel) {
      setEditingSvg(editIconInfo.svg)
      setOriginalInfo(editIconInfo);
    }
    return () => {
      setEditingSvg("");
      setSvgError("");
      setOriginalInfo("");
      didCancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editIconInfo.id]);

  useEffect(() => {
    if(creatingIcon) {
      setUpdatedName({ value: "", finalValue: "" });
      setUpdatedSvg("");
    }
    if(editingIcon) {
      setEditingSvg(editIconInfo.svg);
      setUpdateDisabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatingIcon, editingIcon])

  const checkBeforeLeave = () => {
    if(hasUnsavedChanges) {
      setShowManualModel(true);
    } else {
      onBack();
    }
  }

  const onBack = () => {
    onCancel();
    backBtn();
  };

  const handleUnsavedLeave = () => {
    setShowManualModel(false);
    onBack();
  }

  const handleUnsavedCancel = () => {
    setShowManualModel(false);
  }

  useEffect(() => {
    if(!updateDisabled) {
      setHasUnsavedChanges(true);
    } else if(!createNewDisabled){
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [createNewDisabled, updateDisabled])

  return (
    <>
      <div className="flex-inline mb-4">
        <button type="button" className="btn-back-icons" onClick={checkBeforeLeave}>
          <i className="bi bi-chevron-left"></i> Back
        </button>
        <h3>System Icons</h3>
      </div>
      <div className="icon-list-section">
        <div>
          <ListSvgIcons
            iconList={listOfIcons}
            deleteIcon={deleteIcon}
            editSvgIcon={editSvgIcon}
          />
        </div>
        <div className="right-panel new-system-icon-lg">
          {!editingIcon && (
            <button onClick={onCreateNew} disabled={creatingIcon}>
              Create New Icon
            </button>
          )}
          {creatingIcon && !editingIcon && (
            <div className="create-new-section new-system-icon-sm">
              <div>
                <span className="label">Name:</span>
                <input
                  type="text"
                  value={updatedName.value}
                  onChange={(e) => onCreateNewName(e)}
                  onBlur={onSaveNewName}
                />
                <span className="error-msg">{nameError}</span>
              </div>
              <div>
                <span className="label">Icon Preview:</span>
                {updatedSvg && (
                  <>
                    <img
                      className="icon-preview"
                      height="40"
                      width="40"
                      src={getSvgImgSrc(updatedSvg)}
                      alt="icon"
                    />
                    <span className="icon-preview-note">
                      <u>Note</u>: The preview displays the icon as it will show
                      once created. Please make sure it displays correctly here.
                    </span>
                  </>
                )}
              </div>
              <div>
                <span className="label">SVG:</span>
                <textarea
                  placeholder="Place SVG code here."
                  onChange={onCreateNewSvg}
                />
                <span className="error-msg">{svgError}</span>
              </div>
              <div className="edit-btn-section">
                <button onClick={onCancel}>Cancel</button>
                <button className="promote" onClick={onSubmitNew} disabled={createNewDisabled}>
                  Save
                </button>
              </div>
            </div>
          )}

          {editingIcon && (
            <div className="new-system-icon-sm">
              <div>
                <span className="label">Edit Name:</span>
                <input
                  type="text"
                  value={editIconInfo.name}
                  onChange={onEditName}
                />
                <span className="error-msg">{nameError}</span>
              </div>
              <div>
                <span className="label">Icon Preview:</span>
                {editingSvg && (
                  <>
                    <img
                      className="icon-preview"
                      height="40"
                      width="40"
                      src={getSvgImgSrc(editingSvg)}
                      alt="icon"
                    />
                    <span className="icon-preview-note">
                      <u>Note</u>: The preview displays the icon as it will show
                      once created. Please make sure it displays correctly here.
                    </span>
                  </>
                )}
              </div>
              <div>
                <span className="label">SVG:</span>
                <textarea
                  placeholder="Paste SVG code here."
                  value={editingSvg}
                  onChange={onEditSvg}
                />
                <span className="error-msg">{svgError}</span>
              </div>
              <div className="edit-btn-section">
                <button onClick={onCancel}>Cancel</button>
                <button className="promote" onClick={onSubmitEdit} disabled={updateDisabled}>
                  Update
                </button>
              </div>
            </div>
          )}
        </div>
        <UnsavedChanges hasUnsavedChanges={hasUnsavedChanges} />
        <UnsavedChangesManual showModal={showManualModel} confirmLeave={handleUnsavedLeave} cancelLeave={handleUnsavedCancel} />
      </div>
    </>
  );
}

export default SystemIcons;
